// 標高取得とコースタイム計算
//
// 標高API: 国土地理院の標高取得 API(getelevation.php)
//   - 1点ずつしか取得できないため、並列リクエストでバッチ化
//   - レート制限を考慮し、並列数 4、ウェイト 100ms
//   - オフライン時はキャッシュなし(空値返却)
//
// コースタイム: 山と高原地図方式の簡易版
//   - 水平距離 / 4 km/h
//   - 累積登り標高 / 300 m/h
//   - 累積下り標高 / 600 m/h
//   - これらを合算
//
// より精密な Tobler's hiking function:
//   v = 6 * exp(-3.5 * |slope + 0.05|) km/h
// は P3 以降のオプション

import { haversineMeters } from './tracking.js';

const ELEV_API = 'https://cyberjapandata2.gsi.go.jp/general/dem/scripts/getelevation.php';
const CONCURRENCY = 4;
const WAIT_MS = 100;
const TIMEOUT_MS = 8000;

// 単一点の標高取得
//   返却: { elevation: 数値 or null, hsrc: データソース or null }
//   API レスポンス: { elevation: 1234.5, hsrc: 'DEM5A' }
//   失敗時: null
async function getOneElevation(lat, lng, signal) {
  const url = `${ELEV_API}?lon=${lng.toFixed(6)}&lat=${lat.toFixed(6)}&outtype=JSON`;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: signal || ctrl.signal });
    if (!res.ok) return null;
    const data = await res.json();
    const elev = parseFloat(data.elevation);
    return Number.isFinite(elev) ? { elevation: elev, hsrc: data.hsrc || null } : null;
  } catch (e) {
    return null;
  } finally {
    clearTimeout(t);
  }
}

// 複数点の標高をバッチ取得
//   points: [{lat, lng}]
//   onProgress({done, total}): 進捗コールバック
//   signal: AbortSignal
//   返却: points と同じ長さの配列、各要素は数値 or null
export async function batchGetElevations(points, { onProgress, signal } = {}) {
  const results = new Array(points.length).fill(null);
  let done = 0;
  let cursor = 0;

  async function worker() {
    while (cursor < points.length) {
      if (signal && signal.aborted) return;
      const i = cursor++;
      const p = points[i];
      const r = await getOneElevation(p.lat, p.lng, signal);
      results[i] = r ? r.elevation : null;
      done++;
      if (onProgress) onProgress({ done, total: points.length });
      if (WAIT_MS > 0) await new Promise((r) => setTimeout(r, WAIT_MS));
    }
  }

  const workers = Array.from({ length: CONCURRENCY }, () => worker());
  await Promise.all(workers);
  return results;
}

// ============ コースタイム計算 ============

// シンプル方式
// waypoints: [{lat, lng, ele}]
// 返却: {
//   segments: [{distance, elevGain, elevLoss, time}],
//   total: {distance, elevGain, elevLoss, time}
// }
export function calcCourseTime(waypoints) {
  const segments = [];
  let totalDistance = 0;
  let totalElevGain = 0;
  let totalElevLoss = 0;
  let totalTime = 0;

  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const d = haversineMeters(a.lat, a.lng, b.lat, b.lng);
    let elevGain = 0;
    let elevLoss = 0;
    if (typeof a.ele === 'number' && typeof b.ele === 'number') {
      const diff = b.ele - a.ele;
      if (diff > 0) elevGain = diff;
      else elevLoss = -diff;
    }
    // 時間 (秒)
    // 水平: d / 4km/h = d * 3600 / 4000
    // 登り: gain / 300m/h = gain * 3600 / 300
    // 下り: loss / 600m/h = loss * 3600 / 600
    const flatTime = d / (4000 / 3600); // 秒
    const upTime = elevGain / (300 / 3600);
    const downTime = elevLoss / (600 / 3600);
    const segTime = flatTime + upTime + downTime;

    segments.push({
      distance: d,
      elevGain,
      elevLoss,
      time: segTime,
    });

    totalDistance += d;
    totalElevGain += elevGain;
    totalElevLoss += elevLoss;
    totalTime += segTime;
  }

  return {
    segments,
    total: {
      distance: totalDistance,
      elevGain: totalElevGain,
      elevLoss: totalElevLoss,
      time: Math.floor(totalTime),
    },
  };
}

// 線形補間で waypoints を密にして滑らかな標高プロファイル用の点列を作る
// stepMeters: 区間ごとに何メートル間隔で点を打つか
export function densifyWaypoints(waypoints, stepMeters = 200) {
  const out = [];
  if (waypoints.length === 0) return out;
  out.push({ ...waypoints[0], cumDist: 0 });
  let cumDist = 0;

  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const d = haversineMeters(a.lat, a.lng, b.lat, b.lng);
    const steps = Math.max(1, Math.ceil(d / stepMeters));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const lat = a.lat + (b.lat - a.lat) * t;
      const lng = a.lng + (b.lng - a.lng) * t;
      const distFromA = d * t;
      cumDist = (i === 1 ? 0 : out[out.length - 1].cumDist) + 0; // 累積は最後で計算
      out.push({ lat, lng, cumDist: out[out.length - 1].cumDist + (s === 1 ? distFromA : d / steps) });
    }
  }
  return out;
}

// 標高プロファイル(SVG path d 属性)を生成
//   profile: [{cumDist, ele}]
//   width, height: SVG 寸法(px)
//   padding: 内側余白
export function profileToSvg(profile, width = 320, height = 120, padding = 6) {
  if (!profile || profile.length < 2) return { path: '', area: '', minEle: 0, maxEle: 0 };

  const elevs = profile.map((p) => p.ele).filter((e) => typeof e === 'number');
  if (elevs.length < 2) return { path: '', area: '', minEle: 0, maxEle: 0 };

  const minEle = Math.min(...elevs);
  const maxEle = Math.max(...elevs);
  const range = Math.max(1, maxEle - minEle);
  const maxDist = profile[profile.length - 1].cumDist;

  const xs = (d) => padding + ((width - padding * 2) * d) / Math.max(1, maxDist);
  const ys = (e) => padding + (height - padding * 2) * (1 - (e - minEle) / range);

  const pts = profile.filter((p) => typeof p.ele === 'number');
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xs(p.cumDist).toFixed(1)} ${ys(p.ele).toFixed(1)}`).join(' ');
  const area = `${path} L ${xs(pts[pts.length - 1].cumDist).toFixed(1)} ${height - padding} L ${xs(pts[0].cumDist).toFixed(1)} ${height - padding} Z`;

  return { path, area, minEle, maxEle, maxDist };
}
